// Firestore reads for the denormalized `websiteAdsV2` collection (#487).
//
// One doc per ad (doc id = sourceId), covering both sources
// (newarcHome migrated ads + live-synced immaginacasaAds), with prefetched
// image URLs and an embedded agency block — a card or a detail page renders
// from a single document read. Write access is cloud-functions only.

import firebase from 'firebase/compat/app';
import { COLLECTION_WEBSITE_ADS_V2 } from '../app_const';
import WebsiteAdV2 from '../classes/WebsiteAdV2';

// Firestore `in` queries are capped (10 in older SDKs); chunk to stay safe.
const IN_QUERY_CHUNK = 10;

// The client-side exclusions of every listing, shared by listAds() and
// listAdsPublishedAfter() so the "nuovi annunci" pill never surfaces an ad the
// grid would not show (see listAds for why they are not `where`s).
function isListed(ad) {
    return !ad.parentAdId && !ad.isDelisted;
}

// The published catalogue, newest first. Pass `agencyId` for the agency
// listing (filters on the embedded agency block). Both shapes are backed by
// composite indexes, which are versioned and deployed from newarc-platform.
//
// Single entry point for every listing surface — the /annunci grid AND its map
// (CompraRedesign.vue) and the agency showcase (OptimisedAgencyPropertyListing
// .vue) — so the exclusions below cover all three from one place.
//
// `source` (optional) is forwarded to get(): the /annunci grid passes
// 'server' so an offline client fails loudly instead of resolving with the
// EMPTY cached snapshot (a false "Nessun risultato"). Omitted = SDK default.
export async function listAds({ agencyId = null, source = null } = {}) {
    const db = firebase.firestore();

    let query = db.collection(COLLECTION_WEBSITE_ADS_V2)
        .where('isArchived', '==', false)
        .where('isActive', '==', true);

    if (agencyId) {
        query = query.where('agency.agencyId', '==', agencyId);
    }

    const snapshot = await query.orderBy('publicationDate', 'desc').get(source ? { source } : undefined);
    // #599: a multi-taglio ad's children (parentAdId set, see
    // newarc-cloud-functions#216) must not surface as cards of their own next
    // to the parent — the parent already advertises them ("3 tipologie / da
    // X€") and the "Tipologie" block links each one.
    //
    // Filtered client-side on purpose, NOT with a `where`: a
    // `where('parentAdId','==',null)` would (a) need a third composite index
    // (only two back these queries today) and (b) NOT match the docs that
    // lack the field entirely — which is every ad predating #216 — so it would
    // also require backfilling `parentAdId: null` onto all existing parents.
    //
    // #782: same for a sold ad once websiteSync has flagged it `delisted` (72 h
    // after the sale): out of every list, while its page stays reachable by
    // link (getAd) and from the liked ads (listAdsByIds). A `where` on
    // market.delisted would miss every doc without the field — all of them
    // until websiteSync writes it.
    return snapshot.docs
        .map((doc) => new WebsiteAdV2(doc.data()))
        .filter(isListed);
}

// Ads FIRST published after `since` (epoch ms), newest first: the live check
// of the /annunci "nuovi annunci" pill (#774). Same equality filters and
// orderBy as listAds(), so the existing composite index serves it, and the
// range on the ordered field keeps each check down to the few new docs (an
// empty result costs a single read). Same taglio and delisted exclusions as
// listAds().
export async function listAdsPublishedAfter(since, { source = null } = {}) {
    const db = firebase.firestore();
    const snapshot = await db.collection(COLLECTION_WEBSITE_ADS_V2)
        .where('isArchived', '==', false)
        .where('isActive', '==', true)
        .where('publicationDate', '>', since)
        .orderBy('publicationDate', 'desc')
        .get(source ? { source } : undefined);
    return snapshot.docs
        .map((doc) => new WebsiteAdV2(doc.data()))
        .filter(isListed);
}

// Single ad for the detail page (/annunci/:docId). Doc id = sourceId, so the
// legacy /annunci/{newarcHomeId} URLs keep resolving. Returns null when the
// doc is missing (caller falls back to the legacy fetch during transition).
export async function getAd(adId) {
    const db = firebase.firestore();
    const doc = await db.collection(COLLECTION_WEBSITE_ADS_V2).doc(adId).get();
    return doc.exists ? new WebsiteAdV2(doc.data()) : null;
}

// The liked-ads lookup: fetch ads by their source ids in chunked `in`
// queries (the v1 code sent one unbounded `in`, which breaks past the cap).
//
// #599: the `parentAdId` exclusion listAds() applies does NOT apply here. A
// taglio has its own page, so a user can like it from there; dropping children
// would silently empty their "preferiti". Nor does the delisted one (#782): a
// liked ad that sold stays in the "preferiti", badged "Venduto".
export async function listAdsByIds(ids) {
    const wanted = (ids || []).filter(Boolean);
    if (wanted.length === 0) return [];

    const db = firebase.firestore();
    const chunks = [];
    for (let i = 0; i < wanted.length; i += IN_QUERY_CHUNK) {
        chunks.push(wanted.slice(i, i + IN_QUERY_CHUNK));
    }

    const snapshots = await Promise.all(
        chunks.map((chunk) =>
            db.collection(COLLECTION_WEBSITE_ADS_V2)
                .where('sourceId', 'in', chunk)
                .get()
        )
    );

    const ads = [];
    snapshots.forEach((snapshot) => {
        snapshot.forEach((doc) => ads.push(new WebsiteAdV2(doc.data())));
    });
    // #526: archived/regressed ads keep their doc but must not resurface
    // through liked lists (excluded from listings, feeds, recommendations).
    return ads.filter((ad) => ad.isAvailable());
}
